<template>
  <div id="settlementEdit">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="title">
        <i class="dot"></i>
        供应商结算
      </div>
      <div class="header-actions" v-if="slForm.status">
        <el-tag :type="statusTagType" size="medium" effect="dark">
          {{ statusText }}
        </el-tag>
      </div>
    </div>

    <!-- 驳回提醒 -->
    <el-alert
      v-if="slForm.status === 'ERROR' && slForm.errorMessage"
      type="error"
      :title="'驳回原因：' + slForm.errorMessage"
      show-icon
      :closable="false"
      style="margin-bottom: 16px"
    ></el-alert>

    <!-- 表单区域 -->
    <el-form
      ref="slForm"
      :model="slForm"
      :rules="rules"
      label-position="top"
      v-loading="loading"
      :element-loading-text="loadingText"
      class="settlement-form"
    >
      <!-- 基本信息 -->
      <div class="form-section">
        <div class="section-title">基本信息</div>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="收款方式" prop="collectWay">
              <el-select
                v-model="slForm.collectWay"
                placeholder="请选择收款方式"
                :disabled="isLocked"
              >
                <el-option
                  v-for="item in collectWayEnums"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="费用类型" prop="feeType">
              <el-select v-model="slForm.feeType" placeholder="请选择费用类型" :disabled="isLocked">
                <el-option value="0" label="会议类"></el-option>
                <el-option value="1" label="非会议类"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="项目名称">
              <el-input v-model="slForm.projectName" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="供应商名称">
              <el-input v-model="slForm.supplierName" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="本次收款金额">
              <el-input v-model="slForm.applyMoney" disabled>
                <template slot="append">元</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="金蝶费用类型">
              <el-select
                v-model="slForm.feeTypeNo"
                filterable
                clearable
                placeholder="请选择"
                :disabled="isLocked"
              >
                <el-option
                  v-for="item in expenseItem"
                  :key="item.expenseItemID"
                  :label="item.expenseItem"
                  :value="item.expenseItemID"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="金蝶项目编号">
              <el-input
                v-model="slForm.projectNo"
                placeholder="请输入金蝶项目编号"
                :disabled="isLocked"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="发票类型" prop="receiptType">
              <el-select v-model="slForm.receiptType" placeholder="请选择" :disabled="isLocked">
                <el-option value="0" label="电子发票"></el-option>
                <el-option value="1" label="纸质发票"></el-option>
                <el-option value="2" label="电子+纸质发票"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="快递单号">
              <el-input
                v-model="slForm.deliveryNum"
                placeholder="邮寄纸质材料快递单号（若无不填写）"
                :disabled="isLocked"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 会议场次选择 -->
        <el-row>
          <el-col :span="24">
            <el-form-item label="本次结算的会议场次">
              <el-select
                v-model="slForm.meetingIds"
                multiple
                :collapse-tags="false"
                placeholder="请选择"
                :disabled="isLocked"
              >
                <el-option
                  v-for="item in meetingList"
                  :key="item.id"
                  :label="item.meetingTitle"
                  :value="Number(item.id)"
                >
                  {{ item.meetingTitle }} / {{ $moment && $moment(item.updateTime).format('lll') }}
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- 支出明细表 -->
      <div class="form-section">
        <div class="section-title">
          支出明细
          <el-button
            v-if="!isLocked"
            type="text"
            icon="el-icon-plus"
            @click="addSpendingRow"
          >新增行</el-button>
        </div>
        <el-table :data="slForm.spendingDetailDtos" border size="small" class="spending-table">
          <el-table-column label="序号" type="index" width="60" align="center"></el-table-column>
          <el-table-column label="支出明细" align="center">
            <template slot-scope="scope">
              <el-select
                v-model="scope.row.spendingProjectCode"
                filterable
                clearable
                placeholder="请选择费用类型"
                :disabled="isLocked || isFixedRow(scope.row)"
                @change="onSpendingTypeChange"
              >
                <el-option
                  v-for="cat in optionalCategories"
                  :key="cat.code"
                  :label="cat.label"
                  :value="cat.legacyCode"
                ></el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="金额（元）" width="200" align="center">
            <template slot-scope="scope">
              <el-input
                type="number"
                v-model.number="scope.row.money"
                placeholder="0.00"
                :min="0"
                :disabled="isLocked || isFixedRow(scope.row)"
                @input="recalcTotal"
              >
                <template slot="append">元</template>
              </el-input>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" align="center">
            <template slot-scope="scope">
              <el-button
                v-if="!isLocked && !isFixedRow(scope.row)"
                type="text"
                size="mini"
                class="danger-text"
                @click="removeSpendingRow(scope.$index)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="total-bar">
          合计本次结算金额：<b>{{ slForm.applyMoney || 0 }}</b> 元
        </div>
      </div>

      <!-- ========== 结算材料上传区域（核心创新） ========== -->
      <div class="form-section materials-section">
        <div class="section-title">
          <i class="el-icon-folder-opened"></i>
          结算材料上传
          <span class="material-tip">
            （必传类别已展开，其余按支出明细自动展开）
          </span>
        </div>

        <!-- 遍历激活的费用分类，渲染 FeeSlotPanel -->
        <fee-slot-panel
          v-for="cat in activeCategories"
          :key="cat.code"
          :category="cat"
          :mode="isLocked ? 'view' : 'edit'"
          :can-edit="!isLocked"
          :files-map="getCategoryFiles(cat.code)"
          :conditions="meetingConditions"
          :default-expanded="cat.defaultExpanded"
          @file-added="onFileAdded"
          @file-removed="onFileRemoved"
          @file-preview="onFilePreview"
        />
      </div>

      <!-- 提交按钮 -->
      <div class="form-actions" v-if="!isLocked">
        <el-button type="primary" @click="saveForm" :loading="loading">
          保存信息
        </el-button>
        <el-button
          type="success"
          @click="submitForm"
          :loading="loading"
          :disabled="!canSubmit"
        >
          提交收款申请
        </el-button>
      </div>
    </el-form>

    <!-- 文档预览弹窗 -->
    <el-dialog
      v-if="previewVisible"
      :visible.sync="previewVisible"
      title="文档预览"
      width="80%"
      custom-class="preview-dialog"
    >
      <img v-if="isImageUrl(previewUrl)" :src="previewUrl" style="width:100%">
      <iframe
        v-else
        :src="previewUrl.endsWith('.pdf') ? previewUrl : `https://view.officeapps.live.com/op/view.aspx?src=${previewUrl}`"
        style="width:100%;height:80vh;border:none"
      ></iframe>
    </el-dialog>
  </div>
</template>

<script>
import FeeSlotPanel from '@/components/FeeSlotPanel.vue'
import {
  FEE_CATEGORIES,
  getOptionalCategories,
  getActiveCategories,
  getCategoryByLegacyCode,
  validateCategory,
  LEGACY_CODE_MAP,
} from '@/config/fee-slot-config.js'
import {
  getSpendingOrderDetail,
  submitSpendingOrder,
} from '@/request/api'

export default {
  name: 'SettlementEdit',
  components: { FeeSlotPanel },
  data() {
    return {
      loading: false,
      loadingText: '',
      slForm: {
        collectWay: '',
        feeType: '',
        projectName: '',
        supplierName: '',
        applyMoney: 0,
        receiptType: '',
        deliveryNum: '',
        meetingIds: [],
        feeTypeNo: '',
        projectNo: '',
        spendingDetailDtos: [],
        status: '',
        errorMessage: '',
      },
      rules: {
        collectWay: [{ required: true, message: '请选择收款方式', trigger: 'change' }],
        feeType: [{ required: true, message: '请选择费用类型', trigger: 'change' }],
        receiptType: [{ required: true, message: '请选择发票类型', trigger: 'change' }],
      },
      collectWayEnums: [
        { value: '0', label: '一次性收款并结项' },
        { value: '1', label: '首次收款' },
        { value: '2', label: '阶段收款' },
        { value: '3', label: '尾款' },
        { value: '4', label: '据实结算' },
      ],
      meetingList: [],
      expenseItem: [],
      // 材料文件存储：{ categoryCode: { slotId: [fileObj, ...] } }
      materialFiles: {},
      previewVisible: false,
      previewUrl: '',
    }
  },
  computed: {
    /** 页面是否锁定（已提交/审核通过等状态不可编辑） */
    isLocked() {
      return ['SUBMITTED', 'APPROVED', 'SETTLED', 'K3_PUSHED'].includes(this.slForm.status)
    },
    /** 状态标签类型 */
    statusTagType() {
      const map = { DRAFT: 'warning', REJECTED: 'danger', SUBMITTED: 'info', AUDITING: 'info', APPROVED: 'success', SETTLED: 'success' }
      return map[this.slForm.status] || 'info'
    },
    /** 状态文字 */
    statusText() {
      const map = {
        DRAFT: '草稿', SUBMITTED: '已提交', AUDITING: '审核中',
        APPROVED: '审核通过', REJECTED: '审核驳回', K3_PUSHED: '已推金蝶', SETTLED: '已结算',
      }
      return map[this.slForm.status] || this.slForm.status
    },
    /** 可选费用类型列表（用于支出明细下拉框 — 排除 GENERAL/BASIC_INFO） */
    optionalCategories() {
      return getOptionalCategories()
    },
    /** 当前已选的费用类型编码列表 */
    selectedFeeCodes() {
      return this.slForm.spendingDetailDtos
        .map(row => LEGACY_CODE_MAP[row.spendingProjectCode])
        .filter(Boolean)
    },
    /** 当前激活的费用分类（必传 + 已选） */
    activeCategories() {
      return getActiveCategories(this.selectedFeeCodes)
    },
    /** 会议条件（线上/线下） */
    meetingConditions() {
      // TODO: 从会议数据中判断线上/线下
      return { meetingMode: 'OFFLINE' }
    },
    /** 能否提交 */
    canSubmit() {
      if (!this.slForm.id) return false
      if (this.slForm.spendingDetailDtos.length === 0) return false
      return true
    },
  },
  created() {
    this.projectId = this.$route.query.pId
    this.meetingId = this.$route.query.mId
    this.orderId = this.$route.query.orderId

    this.loadFormData()
  },
  methods: {
    // ====== 数据加载 ======
    async loadFormData() {
      try {
        const queryOrderId = this.orderId || 1
        const res = await getSpendingOrderDetail(queryOrderId)
        if (res.code === 200 && res.data) {
          const detail = res.data
          const order = detail.order || {}
          const items = detail.items || []
          // 映射到表单格式
          this.slForm = {
            id: order.id,
            collectWay: order.collectWay || '0',
            feeType: '0',
            projectName: order.remark || '项目-' + order.projectId,
            supplierName: order.supplierName || '',
            applyMoney: order.orderAmount || 0,
            receiptType: '0',
            deliveryNum: '',
            feeTypeNo: '',
            projectNo: order.orderNo || '',
            status: order.status || 'DRAFT',
            errorMessage: order.status === 'REJECTED' ? order.remark : '',
            meetingIds: order.meetingId ? [order.meetingId] : [],
            spendingDetailDtos: items.map(item => ({
              id: String(item.id),
              spendingProjectCode: item.feeType,
              money: item.amount,
              detailType: 'C',
            })),
          }
        }
      } catch (e) { console.error('loadFormData error:', e) }
    },


    // ====== 支出明细操作 ======
    addSpendingRow() {
      this.slForm.spendingDetailDtos.push({
        id: null,
        spendingProjectCode: null,
        money: 0,
        detailType: 'C',
      })
    },
    removeSpendingRow(idx) {
      this.slForm.spendingDetailDtos.splice(idx, 1)
      this.recalcTotal()
    },
    isFixedRow(row) {
      return ['1', '39', '40', '41'].includes(row.spendingProjectCode)
    },
    recalcTotal() {
      this.slForm.applyMoney = this.slForm.spendingDetailDtos.reduce(
        (sum, item) => sum + (Number(item.money) || 0), 0
      ).toFixed(2)
    },
    onSpendingTypeChange() {
      // 费用类型变化时，activeCategories 会自动更新（computed 响应式）
      this.$forceUpdate()
    },

    // ====== 材料文件操作 ======
    getCategoryFiles(categoryCode) {
      return this.materialFiles[categoryCode] || {}
    },
    onFileAdded({ categoryCode, slotId, file }) {
      if (!this.materialFiles[categoryCode]) {
        this.$set(this.materialFiles, categoryCode, {})
      }
      if (!this.materialFiles[categoryCode][slotId]) {
        this.$set(this.materialFiles[categoryCode], slotId, [])
      }
      this.materialFiles[categoryCode][slotId].push(file)
    },
    onFileRemoved({ categoryCode, slotId, fileIdx }) {
      if (this.materialFiles[categoryCode] && this.materialFiles[categoryCode][slotId]) {
        this.materialFiles[categoryCode][slotId].splice(fileIdx, 1)
      }
    },
    onFilePreview(url) {
      if (typeof url === 'object') url = url.url || url
      this.previewUrl = url
      this.previewVisible = true
    },
    isImageUrl(url) {
      return /\.(jpg|jpeg|png|bmp|gif)/i.test(url)
    },

    // ====== 表单保存/提交 ======
    async saveForm() {
      this.$message.info('保存功能对接中...')
    },
    async submitForm() {
      // 校验必填材料
      const missingAll = []
      for (const cat of this.activeCategories) {
        const filesForCat = this.getCategoryFiles(cat.code)
        const result = validateCategory(cat, filesForCat, this.meetingConditions)
        if (!result.complete) {
          missingAll.push(...result.missing.map(m => `【${cat.label}】${m}`))
        }
      }
      if (missingAll.length > 0) {
        this.$alert(
          `以下必填材料缺失：\n${missingAll.join('\n')}`,
          '材料不完整',
          { type: 'warning', confirmButtonText: '知道了' }
        )
        return
      }

      this.$confirm('确认提交收款申请？提交后不可修改。', '提示', {
        confirmButtonText: '确认提交',
        cancelButtonText: '取消',
        type: 'info',
      }).then(async () => {
        this.loading = true
        this.loadingText = '正在提交...'
        try {
          const res = await submitSpendingOrder(this.slForm.id)
          if (res.code === 200) {
            this.$message.success('提交成功')
            this.loadFormData()
          } else {
            this.$message.error(res.msg || '提交失败')
          }
        } catch (e) {
          this.$message.error('提交异常')
        } finally {
          this.loading = false
        }
      }).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
#settlementEdit {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-bottom: 16px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);

  .title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    position: relative;
    padding-left: 24px;

    .dot {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #3563e9;
      border: 4px solid #c2d0f8;
    }
  }
}

.settlement-form {
  .form-section {
    background: #fff;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);

    .section-title {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      align-items: center;
      gap: 8px;

      .material-tip {
        font-size: 12px;
        color: #909399;
        font-weight: normal;
      }
    }
  }

  .el-select {
    width: 100%;
  }
}

.spending-table {
  margin-bottom: 12px;
}

.total-bar {
  text-align: right;
  padding: 8px 0;
  font-size: 14px;
  color: #606266;

  b {
    color: #e6a23c;
    font-size: 16px;
  }
}

.danger-text {
  color: #f56c6c !important;
}

.materials-section {
  .section-title i {
    color: #409eff;
  }
}

.form-actions {
  background: #fff;
  border-radius: 10px;
  padding: 16px 20px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: center;
  gap: 16px;
}

.preview-dialog {
  .el-dialog__body {
    padding: 10px;
  }
}
</style>
